import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import {AbstractControl, AsyncValidatorFn, ValidationErrors} from '@angular/forms';
import {Observable, of, timer} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {CertificateCreateRequest, CertificateItemDto, GdxJuristicProfileResponse} from '../../../models/generated-models';
import {IGNORE_422_ERROR} from '../../shared/commons/interceptors/http422-interceptor';

@Injectable({providedIn: 'root'})
export class UserCertificateApiService {
  private readonly http = inject(HttpClient);

  listCertificates(): Observable<CertificateItemDto[]> {
    return this.http.get<CertificateItemDto[]>(`${environment.serverUrl}/api/certificates`);
  }

  syncCertificates(): Observable<CertificateItemDto[]> {
    return this.http.post<CertificateItemDto[]>(`${environment.serverUrl}/api/certificates/sync`, {});
  }

  // Sync for the business taxId from the add-business form instead of the
  // current user's cardNo (the caller verifies the user via ThaiD first).
  syncCertificatesByTaxId(taxId: string): Observable<CertificateItemDto[]> {
    return this.http.post<CertificateItemDto[]>(`${environment.serverUrl}/api/certificates/sync/taxid`, {taxId});
  }

  createCertificate(request: CertificateCreateRequest): Observable<CertificateItemDto> {
    return this.http.post<CertificateItemDto>(`${environment.serverUrl}/api/certificates`, request);
  }

  updateCertificate(id: number, request: CertificateCreateRequest): Observable<CertificateItemDto> {
    return this.http.put<CertificateItemDto>(`${environment.serverUrl}/api/certificates/${id}`, request);
  }

  isCertificateNoAvailable(certificateNo: string, excludeId?: number): Observable<boolean> {
    let params = new HttpParams().set('certificateNo', certificateNo);
    if (excludeId !== undefined && excludeId !== null) {
      params = params.set('excludeId', excludeId);
    }
    return this.http.get<boolean>(`${environment.serverUrl}/api/certificates/validate`, {
      params,
      context: new HttpContext().set(IGNORE_422_ERROR, true)
    });
  }

  certificateNoValidator(excludeId?: number): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const certificateNo = `${control.value ?? ''}`.trim();
      if (!certificateNo) {
        return of(null);
      }

      return timer(300).pipe(
        switchMap(() => this.isCertificateNoAvailable(certificateNo, excludeId)),
        map(isAvailable => isAvailable ? null : {certificateNoTaken: true}),
        catchError(() => of(null)),
        take(1)
      );
    };
  }

  /** The certificate's scan (PDF / image, ≤ 5 MB); replaces an earlier one. Work item 170. */
  uploadAttachment(id: number, file: File): Observable<CertificateItemDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CertificateItemDto>(`${environment.serverUrl}/api/certificates/${id}/attachment`, formData);
  }

  removeAttachment(id: number): Observable<CertificateItemDto> {
    return this.http.delete<CertificateItemDto>(`${environment.serverUrl}/api/certificates/${id}/attachment`);
  }

  deleteCertificate(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.serverUrl}/api/certificates/${id}`);
  }

  findJuristicProfile(juristicId: string): Observable<GdxJuristicProfileResponse> {
    return this.http.get<GdxJuristicProfileResponse>(`${environment.serverUrl}/api/gdx/juristic-profile`, {
      params: {juristicId},
      context: new HttpContext().set(IGNORE_422_ERROR, true)
    });
  }
}
